import { api, ReportData } from '../api';
import { escapeHtml } from '../lib/utils';
import { toast } from '../components/toast';
import { ProjectContext } from './project';

const RANGES = [
  { key: 'week', label: 'Last week' },
  { key: 'month', label: 'Last month' },
  { key: 'three_months', label: 'Last 3 months' },
  { key: 'total', label: 'All time' },
];

export async function renderReports(
  content: HTMLElement,
  ctx: ProjectContext
): Promise<void> {
  const { project } = ctx;

  content.innerHTML = `<div class="spinner">Loading report…</div>`;
  await renderRange(content, project.id, 'total');
}

async function renderRange(
  content: HTMLElement,
  projectId: string,
  range: string
): Promise<void> {
  content.innerHTML = `<div class="spinner">Loading report…</div>`;

  let report: ReportData;
  try {
    const res = await api.get<ReportData>(`/projects/${projectId}/report?range=${range}`);
    report = res;
  } catch (e: any) {
    content.innerHTML = `
      <div class="error-state">
        <h3>Could not load report</h3>
        <p class="muted">${escapeHtml(e.message)}</p>
      </div>
    `;
    return;
  }

  const members = report.members.sort((a, b) => b.points - a.points);

  content.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;margin-bottom:20px;">
      <div class="range-tabs">
        ${RANGES.map(
          (r) =>
            `<button class="range-tab ${r.key === range ? 'active' : ''}" data-range="${r.key}">${r.label}</button>`
        ).join('')}
      </div>
    </div>

    <div class="stat-grid" style="grid-template-columns:repeat(2,1fr);">
      <div class="stat-card">
        <div class="stat-value">${report.totalPoints}</div>
        <div class="stat-label">Story points completed</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${report.totalCount}</div>
        <div class="stat-label">Tasks completed</div>
      </div>
    </div>

    <div class="chart-grid">
      <div class="chart-card">
        <h3>Points completed per member</h3>
        <div class="chart-box"><canvas id="report-chart"></canvas></div>
      </div>
      <div class="chart-card">
        <h3>Who completed how much</h3>
        ${
          members.length === 0
            ? '<p class="muted">No completed tasks in this period.</p>'
            : `
          <table class="report-table">
            <thead>
              <tr><th>Member</th><th>Tasks</th><th>Story points</th></tr>
            </thead>
            <tbody>
              ${members
                .map(
                  (m) => `
                <tr>
                  <td><strong>${escapeHtml(m.name)}</strong></td>
                  <td>${m.count}</td>
                  <td><span class="points-chip">${m.points} pts</span></td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        `
        }
      </div>
    </div>
  `;

  content.querySelectorAll<HTMLElement>('[data-range]').forEach((btn) => {
    btn.addEventListener('click', () => {
      renderRange(content, projectId, btn.dataset.range!);
    });
  });

  const canvas = content.querySelector('#report-chart') as HTMLCanvasElement;
  if (canvas && members.length > 0) {
    new Chart(canvas, {
      type: 'bar',
      data: {
        labels: members.map((m) => escapeHtml(m.name)),
        datasets: [
          {
            label: 'Story points',
            data: members.map((m) => m.points),
            backgroundColor: '#7c3aed',
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }
}
